import { getLocalTimeZone, parseAbsolute } from "@internationalized/date"
import { useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  Link,
  notFound,
  redirect,
} from "@tanstack/react-router"
import { AutoTextSize } from "auto-text-size"
import { GatedGetConfirmationDetailsFailReason } from "ipctg-inflow-types/InFlowEnums"
import { useTranslation } from "react-i18next"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import {
  formatZonedDateTimeWithTimeZone,
  percentFormatter,
} from "@/utilities/etc"
import { GatedGetConfirmationDetailsError } from "@/utilities/expectedErrors"
import {
  gatedConfirmationDetailsQueryOptions,
  useSendGatedConfirmationDetails,
} from "@/utilities/hooks/queries/gated"
import { useTheme } from "@/utilities/hooks/useTheme"
import useToggle from "@/utilities/hooks/useToggle"
import { log } from "@/utilities/logging"
import {
  normalizedAPILicensePlate,
  normalizeLongStringOfNumbers,
} from "@/utilities/normalizers"

import SendConfirmationViaMethodModal from "../../-components/SendConfirmationViaMethodModal"

export const Route = createFileRoute(
  "/qr/$qrLookupID/gated/register/$sessionID/confirmation"
)({
  staticData: {
    pageName: "Confirmation",
  },
  validateSearch: (
    search: Record<string, unknown>
  ): {
    key: string
  } => {
    return {
      key: (search.key as string) || "",
    }
  },
  beforeLoad: async ({ context, params, search }) => {
    if (!search.key) throw notFound()

    const { receiptID, showReceipt } = await context.queryClient.fetchQuery(
      gatedConfirmationDetailsQueryOptions({
        sessionID: params.sessionID,
        confirmationKey: search.key,
      })
    )

    if (receiptID && showReceipt) {
      throw redirect({
        to: "/qr/$qrLookupID/receipt/$receiptID",
        params: { qrLookupID: params.qrLookupID, receiptID },
        replace: true,
      })
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { sessionID } = Route.useParams()
  const { key } = Route.useSearch()
  const { drawer } = useTheme()
  const { t } = useTranslation(["gated", "common"])
  const { data } = useSuspenseQuery(
    gatedConfirmationDetailsQueryOptions({ sessionID, confirmationKey: key })
  )
  const [isPhoneConfirmationModalOpen, togglePhoneConfirmationModalOpen] =
    useToggle(false)
  const [isEmailConfirmationModalOpen, toggleEmailConfirmationModalOpen] =
    useToggle(false)
  const { mutateAsync, reset, isPending } = useSendGatedConfirmationDetails({
    sessionID,
    confirmationKey: key,
  })

  // Shouldn't happen because that means that it is paid and we redirect them above
  if (!data.confirmationDetails) {
    const error = new GatedGetConfirmationDetailsError(
      GatedGetConfirmationDetailsFailReason.ConfirmationCodeNotFound
    )
    log({
      level: "error",
      flow: "gated:register",
      scope: "validation.confirmation",
      message: "No confirmation details or receipt found for this session",
      meta: { sessionID, data },
      sentry: "exception",
      exception: error,
    })
    throw error
  }

  return (
    <>
      <Heading className="center" level={3} as={1}>
        {t("register.confirmation.header", { ns: "gated" })}
      </Heading>
      <Card>
        <Heading className="center" level={3} as={2}>
          {t("register.confirmation.subhead", { ns: "gated" })}
        </Heading>
        <div className="confirmation-code">
          <AutoTextSize maxFontSizePx={48} mode="boxoneline">
            {normalizeLongStringOfNumbers(
              data.confirmationDetails.confirmationCode
            )}
          </AutoTextSize>
        </div>
        <Button buttonType="primary" onPress={togglePhoneConfirmationModalOpen}>
          <Icon name="speechBubble" slot="left" />
          {t("register.confirmation.textCode", { ns: "gated" })}
        </Button>
      </Card>
      <Heading level={3} style={{ marginTop: "var(--space-8)" }}>
        {t("register.confirmation.sessionDetails.header", { ns: "gated" })}
      </Heading>
      <dl>
        <div>
          <dt>
            <b>
              {t("register.confirmation.sessionDetails.list.licensePlate", {
                ns: "gated",
              })}
            </b>
          </dt>
          <dd>{normalizedAPILicensePlate(data.confirmationDetails.plate)}</dd>
        </div>
        <div>
          <dt>
            <b>
              {t("register.confirmation.sessionDetails.list.lot", {
                ns: "gated",
              })}
            </b>
          </dt>
          <dd>{data.confirmationDetails.lotName}</dd>
        </div>
        <div>
          <dt>
            <b>
              {t("register.confirmation.sessionDetails.list.entryTime", {
                ns: "gated",
              })}
            </b>
          </dt>
          <dd>
            {formatZonedDateTimeWithTimeZone(
              parseAbsolute(
                data.confirmationDetails.entryTime,
                getLocalTimeZone()
              )
            )}
          </dd>
        </div>
        {data.confirmationDetails.paymentMethod && (
          <div>
            <dt>
              <b>
                {t("register.confirmation.sessionDetails.list.paymentMethod", {
                  ns: "gated",
                })}
              </b>
            </dt>
            <dd>{data.confirmationDetails.paymentMethod}</dd>
          </div>
        )}
        {data.confirmationDetails.promoCode && (
          <div style={{ color: "var(--success-10)" }}>
            <dt>
              {t("register.confirmation.sessionDetails.list.promoCode", {
                ns: "gated",
              })}
            </dt>
            <dd style={{ fontWeight: 500 }}>
              {percentFormatter(data.confirmationDetails.promoDiscount)} off
            </dd>
          </div>
        )}
        <div>
          <dt>
            {t("register.confirmation.sessionDetails.list.totalDue", {
              ns: "gated",
            })}
          </dt>
          <dd>
            {data.confirmationDetails.promoDiscount === 1 ? (
              "$0"
            ) : (
              <>
                {t(
                  "register.confirmation.sessionDetails.list.totaledUponExit",
                  { ns: "gated" }
                )}{" "}
                <a
                  href={
                    drawer?.externalLinks?.find((el) => el.type === "rates")
                      ?.link || ""
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("common.seeRates", { ns: "common" })}.
                </a>
              </>
            )}
          </dd>
        </div>
      </dl>
      <Button
        buttonType="default"
        style={{ width: "100%", marginTop: "var(--space-6)" }}
        onPress={toggleEmailConfirmationModalOpen}
      >
        <Icon name="mail" slot="left" />
        {t("register.confirmation.emailCode", { ns: "gated" })}
      </Button>
      <Link
        className="body--2 semi-bold center"
        from={Route.fullPath}
        to="/qr/$qrLookupID/welcome"
      >
        {t("register.confirmation.registerAnother", { ns: "gated" })}
      </Link>
      <SendConfirmationViaMethodModal
        method="phone"
        isOpen={isPhoneConfirmationModalOpen}
        onOpenChange={togglePhoneConfirmationModalOpen}
        defaultInput={""}
        isPending={isPending}
        mutateAsync={mutateAsync}
        resetMutation={reset}
      />
      <SendConfirmationViaMethodModal
        method="email"
        isOpen={isEmailConfirmationModalOpen}
        onOpenChange={toggleEmailConfirmationModalOpen}
        defaultInput={""}
        isPending={isPending}
        mutateAsync={mutateAsync}
        resetMutation={reset}
      />
    </>
  )
}
